import { Link, getRouteApi, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState, useEffect, useRef } from "react";
import { api, API_BASE } from "@/lib/api";
import { cn } from "@/lib/utils";
import { useDebouncedValue } from "@/lib/use-debounced-value";
import { track } from "@/lib/analytics";
import "./index.css";

const routeApi = getRouteApi("/records");

const raceTypeDisplayMap: Record<string, string> = {
  triathlon: "Triathlon",
  duathlon: "Duathlon",
};

function formatRaceTypeLabel(raceType: string | null | undefined): string {
  if (!raceType) return "";

  const normalized = raceType.trim().toLowerCase();
  const mapped = raceTypeDisplayMap[normalized];
  if (mapped) return mapped;

  const kMatch = normalized.match(/^(\d+(?:\.\d+)?)k$/);
  if (kMatch) return `${kMatch[1]}K`;

  return normalized
    .split(/[\s_]+/)
    .filter(Boolean)
    .map((word) => word[0]?.toUpperCase() + word.slice(1))
    .join(" ");
}

export default function SearchPage() {
  const search = routeApi.useSearch();
  const navigate = useNavigate();

  const [query, setQuery] = useState(search.q || "");
  const debouncedQuery = useDebouncedValue(query, 300);
  const [searchType, setSearchType] = useState<"athletes" | "events">(
    search.type || "athletes",
  );
  const [gender, setGender] = useState(search.gender || "");
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    requestAnimationFrame(() => setRevealed(true));
  }, []);

  const [eventRaceType, setEventRaceType] = useState(search.raceType || "");
  const [eventBrand, setEventBrand] = useState(search.brand || "");
  const [swimCourse, setSwimCourse] = useState(search.swimCourse || "");
  const [bikeCourse, setBikeCourse] = useState(search.bikeCourse || "");
  const [runCourse, setRunCourse] = useState(search.runCourse || "");

  const isTriathlon = eventRaceType === "triathlon";

  // Reset triathlon-specific filters when race type changes away from triathlon
  useEffect(() => {
    if (!isTriathlon) {
      setEventBrand("");
      setSwimCourse("");
      setBikeCourse("");
      setRunCourse("");
    }
  }, [isTriathlon]);

  // Sync search state to URL query params (replace to avoid polluting history)
  const isInitialMount = useRef(true);
  const lastSearchEventKey = useRef<string | null>(null);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    navigate({
      to: "/records",
      search: {
        q: query || undefined,
        type: searchType !== "athletes" ? (searchType as "events") : undefined,
        gender: gender || undefined,
        raceType: eventRaceType || undefined,
        brand: eventBrand || undefined,
        swimCourse: swimCourse || undefined,
        bikeCourse: bikeCourse || undefined,
        runCourse: runCourse || undefined,
      },
      replace: true,
    });
  }, [query, searchType, gender, eventRaceType, eventBrand, swimCourse, bikeCourse, runCourse, navigate]);

  const eventRaceTypes = useQuery({
    queryKey: ["search", "event-race-types"],
    queryFn: async () => {
      const response = await fetch(`${API_BASE}/search/event-race-types`);
      if (!response.ok) {
        throw new Error("Failed to load event race types");
      }

      const data = await response.json();
      return Array.isArray(data) ? (data as string[]) : [];
    },
    staleTime: 1000 * 60 * 5,
  });

  const eventBrands = useQuery({
    queryKey: ["search", "event-brands", eventRaceType],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (eventRaceType) params.set("raceType", eventRaceType);
      const response = await fetch(`${API_BASE}/search/event-brands?${params}`);
      if (!response.ok) throw new Error("Failed to load event brands");
      const data = await response.json();
      return Array.isArray(data) ? (data as string[]) : [];
    },
    enabled: isTriathlon,
    staleTime: 1000 * 60 * 5,
  });

  const courseTypes = useQuery({
    queryKey: ["search", "event-course-types"],
    queryFn: async () => {
      const response = await fetch(`${API_BASE}/search/event-course-types`);
      if (!response.ok) throw new Error("Failed to load course types");
      return response.json() as Promise<{
        swim: string[];
        bike: string[];
        run: string[];
      }>;
    },
    enabled: isTriathlon,
    staleTime: 1000 * 60 * 5,
  });

  const athleteSearch = useQuery({
    queryKey: ["search", "athletes", debouncedQuery, gender],
    queryFn: async () => {
      const { data, error } = await api.GET("/search/athletes", {
        params: {
          query: {
            q: debouncedQuery || undefined,
            gender: gender || undefined,
            limit: 20,
            offset: 0,
          },
        },
      });
      if (error) throw error;
      return data;
    },
    enabled: searchType === "athletes" && debouncedQuery.length >= 2,
  });

  const eventSearch = useQuery({
    queryKey: ["search", "events", debouncedQuery, eventRaceType, eventBrand, swimCourse, bikeCourse, runCourse],
    queryFn: async () => {
      const { data, error } = await api.GET("/search/events", {
        params: {
          query: {
            q: debouncedQuery || undefined,
            raceType: eventRaceType || undefined,
            brand: eventBrand || undefined,
            swimCourse: swimCourse || undefined,
            bikeCourse: bikeCourse || undefined,
            runCourse: runCourse || undefined,
            limit: 20,
            offset: 0,
          },
        },
      });
      if (error) throw error;
      return data;
    },
    enabled:
      searchType === "events" && (debouncedQuery.length >= 2 || Boolean(eventRaceType)),
  });

  const results = searchType === "athletes" ? athleteSearch : eventSearch;
  const activeSearchUpdatedAt =
    searchType === "athletes"
      ? athleteSearch.dataUpdatedAt
      : eventSearch.dataUpdatedAt;
  const activeSearchStatus =
    searchType === "athletes" ? athleteSearch.status : eventSearch.status;
  const activeResultCount =
    searchType === "athletes"
      ? (athleteSearch.data?.length ?? 0)
      : (eventSearch.data?.length ?? 0);
  const isSearchEnabled =
    searchType === "athletes"
      ? query.length >= 2
      : query.length >= 2 || Boolean(eventRaceType);

  useEffect(() => {
    if (!isSearchEnabled || activeSearchStatus !== "success") {
      return;
    }

    if (!activeSearchUpdatedAt || activeSearchUpdatedAt <= 0) {
      return;
    }

    const dedupeKey = [
      searchType,
      query,
      gender,
      eventRaceType,
      eventBrand,
      swimCourse,
      bikeCourse,
      runCourse,
      String(activeSearchUpdatedAt),
    ].join("|");

    if (lastSearchEventKey.current === dedupeKey) {
      return;
    }

    lastSearchEventKey.current = dedupeKey;

    track("tr_records_search_executed", {
      search_type: searchType,
      query_text: query,
      query_length: query.length,
      gender_filter: gender || null,
      race_type_filter: eventRaceType || null,
      brand_filter: eventBrand || null,
      swim_course_filter: swimCourse || null,
      bike_course_filter: bikeCourse || null,
      run_course_filter: runCourse || null,
      result_count: activeResultCount,
    });
  }, [
    isSearchEnabled,
    activeSearchStatus,
    activeSearchUpdatedAt,
    activeResultCount,
    searchType,
    query,
    gender,
    eventRaceType,
    eventBrand,
    swimCourse,
    bikeCourse,
    runCourse,
  ]);

  return (
    <div className="search-page">
      {/* Hero */}
      <div
        className={cn(
          "search-hero d8-reveal d8-reveal-delay-1",
          revealed && "revealed",
        )}
      >
        <div className="search-hero-tag">// Endurance Athletics Database</div>
        <h1 className="search-hero-title">
          Search <strong>Records</strong>
        </h1>
      </div>

      {/* Search Panel */}
      <div
        className={cn(
          "search-panel d8-reveal d8-reveal-delay-2",
          revealed && "revealed",
        )}
      >
        {/* Toggles */}
        <div className="search-toggles">
          <button
            type="button"
            className={cn("d8-toggle", searchType === "athletes" && "active")}
            onClick={() => setSearchType("athletes")}
          >
            Athletes
          </button>
          <button
            type="button"
            className={cn("d8-toggle", searchType === "events" && "active")}
            onClick={() => setSearchType("events")}
          >
            Events
          </button>
        </div>

        {/* Search Input */}
        <div className="search-input-wrap">
          <input
            type="text"
            className="search-input"
            placeholder={
              searchType === "athletes"
                ? "Search athletes..."
                : "Search events..."
            }
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <span className="search-icon">ENTER</span>
        </div>

        {/* Filters - conditional based on search type */}
        <div className="search-filters">
          {searchType === "athletes" && (
            <select
              value={gender}
              onChange={(e) => setGender(e.target.value)}
              className="search-select"
            >
              <option value="">All Genders</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
            </select>
          )}

          {searchType === "events" && (
            <select
              value={eventRaceType}
              onChange={(e) => setEventRaceType(e.target.value)}
              className="search-select"
            >
              <option value="">All Race Types</option>
              {eventRaceTypes.data?.map((type) => (
                <option key={type} value={type}>
                  {formatRaceTypeLabel(type)}
                </option>
              ))}
            </select>
          )}

          {searchType === "events" && isTriathlon && (
            <select
              value={eventBrand}
              onChange={(e) => setEventBrand(e.target.value)}
              className="search-select"
            >
              <option value="">All Distances</option>
              {eventBrands.data?.map((brand) => (
                <option key={brand} value={brand}>
                  {brand}
                </option>
              ))}
            </select>
          )}
        </div>

        {searchType === "events" && isTriathlon && (
          <div className="search-filters search-filters-course">
            <select
              value={swimCourse}
              onChange={(e) => setSwimCourse(e.target.value)}
              className="search-select"
            >
              <option value="">Swim Course</option>
              {courseTypes.data?.swim.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>

            <select
              value={bikeCourse}
              onChange={(e) => setBikeCourse(e.target.value)}
              className="search-select"
            >
              <option value="">Bike Course</option>
              {courseTypes.data?.bike.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>

            <select
              value={runCourse}
              onChange={(e) => setRunCourse(e.target.value)}
              className="search-select"
            >
              <option value="">Run Course</option>
              {courseTypes.data?.run.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Results */}
        <div className="search-results d8-scrollable">
          {results.isLoading && (
            <div className="search-status">Searching...</div>
          )}

          {searchType === "athletes" &&
            athleteSearch.data?.map((athlete, index) => (
              <Link
                key={athlete.id}
                to="/athletes/$athleteId"
                params={{ athleteId: athlete.id }}
                className="search-result-row"
                onClick={() =>
                  track("tr_records_result_click", {
                    result_type: "athlete",
                    result_id: athlete.id,
                    position: index + 1,
                    query_text: query,
                    search_type: searchType,
                  })
                }
              >
                <div>
                  <div className="search-res-name">{athlete.fullName}</div>
                  <div className="search-res-meta">
                    {[athlete.city, athlete.state, (athlete as any).representingCountryIso3 ?? athlete.countryIso2]
                      .filter(Boolean)
                      .join(", ")}
                    {athlete.gender && ` \u00b7 ${athlete.gender}`}
                  </div>
                </div>
                <div className="search-res-right">
                  {athlete.gender && (
                    <span className="search-res-badge">{athlete.gender}</span>
                  )}
                  <span className="search-res-value">
                    {athlete.resultCount} race
                    {athlete.resultCount !== 1 ? "s" : ""}
                  </span>
                </div>
              </Link>
            ))}

          {searchType === "events" &&
            eventSearch.data?.map((event, index) => (
              event.kind === "group" ? (
                <Link
                  key={event.id}
                  to="/events/group/$groupId"
                  params={{ groupId: event.id }}
                  className="search-result-row"
                  onClick={() =>
                    track("tr_records_result_click", {
                      result_type: "event_group",
                      result_id: event.id,
                      position: index + 1,
                      query_text: query,
                      search_type: searchType,
                    })
                  }
                >
                  <div>
                    <div className="search-res-name">{event.displayName}</div>
                    <div className="search-res-meta">
                      {[event.brand, "Group"].filter(Boolean).join(" \u00b7 ")}
                    </div>
                  </div>
                  <span className="search-res-value">
                    {event.seriesCount} race
                    {event.seriesCount !== 1 ? "s" : ""}
                  </span>
                </Link>
              ) : (
                <Link
                  key={event.id}
                  to="/events/series/$seriesId"
                  params={{ seriesId: event.id }}
                  search={{ year: undefined }}
                  className="search-result-row"
                  onClick={() =>
                    track("tr_records_result_click", {
                      result_type: "event_series",
                      result_id: event.id,
                      position: index + 1,
                      query_text: query,
                      search_type: searchType,
                    })
                  }
                >
                  <div>
                    <div className="search-res-name">{event.displayName}</div>
                    <div className="search-res-meta">
                      {[event.brand, formatRaceTypeLabel(event.raceType)]
                        .filter(Boolean)
                        .join(" \u00b7 ")}
                    </div>
                  </div>
                  <span className="search-res-value">
                    {event.totalResults > 0
                      ? `${event.totalResults} result${event.totalResults !== 1 ? "s" : ""}`
                      : `${event.totalEvents} event${event.totalEvents !== 1 ? "s" : ""}`}
                  </span>
                </Link>
              )
            ))}

          {results.data && results.data.length === 0 && (
            <div className="search-no-results">
              <div>
                {query
                  ? `No results found for "${query}"`
                  : "No events found for these filters."}
              </div>
              {searchType === "events" ? (
                <Link
                  to="/request-race"
                  search={{
                    race: query || undefined,
                    source: "records",
                  }}
                  className="search-missing-race-link"
                  onClick={() =>
                    track("tr_race_request_cta_click", {
                      source: "records",
                      query_text: query,
                      search_type: searchType,
                      race_type_filter: eventRaceType || null,
                      brand_filter: eventBrand || null,
                      swim_course_filter: swimCourse || null,
                      bike_course_filter: bikeCourse || null,
                      run_course_filter: runCourse || null,
                    })
                  }
                >
                  Missing a race? Request it
                </Link>
              ) : null}
            </div>
          )}

          {query.length > 0 && query.length < 2 && (
            <div className="search-status">
              Type at least 2 characters to search
            </div>
          )}

          {results.data && results.data.length > 0 && (
            <div className="search-count">
              {results.data.length} result{results.data.length !== 1 ? "s" : ""}{" "}
              found
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
